import { Button, Input, ModalV2 } from '@components'
import { FlagWrapper, useBusinessContext } from '@context'
import { createInvitation, IInvitation } from '@query'
import { usePermissionGroups, usePermissions } from '@root/query/hooks'
import { setAPIErrors } from '@utils'
import React, { useEffect } from 'react'
import { Controller, FormProvider, useForm, useWatch } from 'react-hook-form'
import { useTranslation } from 'react-i18next'
import { FaPaperPlane } from 'react-icons/fa'
import { useMutation, useQueryClient } from '@tanstack/react-query'
import styled from 'styled-components'
import { NocfoPermissionGroups } from '@root/constants'
import toast from 'react-hot-toast'
import { PermissionPicker } from './PermissionPicker'
import { BusinessRoleSelector } from './BusinessRoleSelector'

interface Props {
  isVisible: boolean
  onClose?: () => void
}

interface InvitationFormInputs {
  email: string
  permission_group: NocfoPermissionGroups
  permissions: string[]
  tags: string[]
}

export const InvitationModal: React.FC<Props> = ({ isVisible, onClose: _onClose }) => {
  const { slug } = useBusinessContext()
  const queryClient = useQueryClient()
  const [t] = useTranslation()

  const { data: permissions } = usePermissions()
  const { data: permissionGroups } = usePermissionGroups()

  const methods = useForm<InvitationFormInputs>({
    defaultValues: {
      email: '',
      permission_group: NocfoPermissionGroups.READ_ONLY,
      permissions: [],
      tags: []
    }
  })
  const {
    reset,
    register,
    setError,
    setValue,
    handleSubmit,
    control,
    formState: { isSubmitting, errors }
  } = methods

  // Always start from Read only when the modal opens so stale preset/toggle state cannot leak
  // between sessions (the component stays mounted while closed).
  useEffect(() => {
    if (!isVisible) return

    const readOnlyPermissions =
      permissionGroups
        ?.find(({ id }) => id === NocfoPermissionGroups.READ_ONLY)
        ?.permissions.map(({ id }) => id) ?? []

    reset({
      email: '',
      permission_group: NocfoPermissionGroups.READ_ONLY,
      permissions: readOnlyPermissions,
      tags: []
    })
  }, [isVisible, permissionGroups, reset])

  const onClose = () => {
    _onClose?.()
    reset()
  }

  const createInvitationMutation = useMutation<IInvitation, unknown, Partial<IInvitation>>({
    mutationFn: data => createInvitation({ slug }, data),
    ...{
      onSuccess: () => {
        queryClient.invalidateQueries({ queryKey: [slug, 'invitations'] })
        reset()

        toast.success(t('settings.people.invitationSent'))
      },
      onError: error => {
        const { status, data } = (error as any) ?? {}
        if (status === 400) {
          setAPIErrors(data, setError)
        } else {
          toast.error(t('general.error'))
        }
      }
    }
  })

  const selectedPermissionGroup = useWatch({ control, name: 'permission_group' })
  const selectedPermissionIds = useWatch({ control, name: 'permissions' }) || []

  return (
    <ModalV2
      isVisible={isVisible}
      handleOnClose={onClose}
      width={800}
      height={900}
      header={t('settings.people.sendInvitation')}
      footer={
        <ModalFooter>
          <Button type="button" intent="default" onClick={() => onClose()}>
            {t('general.close')}
          </Button>
          <div style={{ flex: 1 }} />
          <Button
            type="submit"
            intent="success"
            form="invitation-form"
            icon={<FaPaperPlane />}
            showSpinner={isSubmitting}
            disabled={
              isSubmitting ||
              (selectedPermissionGroup === NocfoPermissionGroups.CUSTOM &&
                selectedPermissionIds.length === 0)
            }
          >
            {t('settings.people.sendInvitation')}
          </Button>
        </ModalFooter>
      }
    >
      <FormProvider {...methods}>
        <form
          id="invitation-form"
          onSubmit={handleSubmit(data => {
            const payload: Partial<IInvitation> =
              data.permission_group === NocfoPermissionGroups.CUSTOM
                ? data
                : {
                    email: data.email,
                    permission_group: data.permission_group,
                    tags: data.tags
                  }

            return createInvitationMutation.mutateAsync(payload).then(() => onClose())
          })}
        >
          <Input
            label={t('settings.people.email')}
            id="email"
            errors={errors.email}
            required={true}
            {...register('email', {
              required: { value: true, message: t('validation.required') },
              pattern: { value: /^\S+@\S+$/i, message: t('validation.invalidEmail') }
            })}
          />

          <FlagWrapper flag="workflows">
            <Controller
              name="tags"
              control={control}
              render={({ field }) => (
                <BusinessRoleSelector value={field.value ?? []} onChange={field.onChange} />
              )}
            />
          </FlagWrapper>

          <StyledPermissionsHeader>
            <h4>{t('settings.people.permissions')}</h4>
            <PermissionsHint>{t('settings.people.permissionExplanation')}</PermissionsHint>
          </StyledPermissionsHeader>

          <StyledPickerWrapper>
            <PermissionPicker
              groups={permissionGroups ?? []}
              permissions={permissions ?? []}
              selectedGroupId={selectedPermissionGroup}
              selectedPermissionIds={selectedPermissionIds}
              onChange={({ groupId, permissionIds }) => {
                setValue('permission_group', groupId as NocfoPermissionGroups)
                setValue('permissions', permissionIds)
              }}
            />
          </StyledPickerWrapper>
        </form>
      </FormProvider>
      <div style={{ flex: 1 }} />
    </ModalV2>
  )
}

const ModalFooter = styled.div`
  flex: 1;
  display: flex;
  justify-content: space-between;
`
const StyledPickerWrapper = styled.div`
  padding: ${({ theme }) => theme.spacing.sm}rem 0;
`
const StyledPermissionsHeader = styled.div`
  margin-top: ${({ theme }) => theme.spacing.sm}rem;

  h4 {
    margin: 0 0 ${({ theme }) => theme.spacing.xs}rem 0;
  }
`
const PermissionsHint = styled.p`
  margin: 0;
  font-size: ${({ theme }) => theme.fontSize.sm}rem;
  color: ${({ theme }) => theme.colors.iconGray};
  line-height: 1.5;
`
